import { defineStore } from 'pinia';
import { hydrated } from '@/storePinia';
import axios from 'axios';
import useLoader from '@use/useLoader/useLoader';
import { IPersonal, IPersonalRoutes } from './interfaces/IPersonal';

type IPersonalGetters = {
  isAuthorized(state: IPersonal): void | boolean;
};

type IPersonalActions = {
  getData: (payload: { action: string; api?: 'y'; LANGUAGE_ID?: string }) => Promise<void>;
  signUpByPhone: (formData: FormData) => Promise<{ error?: string; code?: boolean } | undefined>;
  signInByPhone: (formData: FormData) => Promise<{ error?: string; code?: boolean } | undefined>;
  confirmPhone: (
    formData: FormData,
    // eslint-disable-next-line camelcase
    callBack: (data: { user_phone: string; user_email?: string }) => void
  ) => Promise<void>;
  signInByPhonePassword: (
    formData: FormData
  ) => Promise<{ status: boolean; text: string } | undefined>;
  sendPasswordRecoveryCheckWordToUserEmail: (
    formData: FormData
  ) => Promise<{ status: boolean; text: string } | undefined>;
  doPasswordRecoveryByCheckWordAndEmail: (
    formData: FormData
  ) => Promise<{ status: boolean; text: string } | undefined>;
  sendRegisterLinkToGlobalUser: (
    formData: FormData
  ) => Promise<{ status: boolean; text: string } | undefined>;
  // registerUkraine: (formData: FormData) => Promise<{ status: boolean; text: string } | undefined>;
};

const loader = useLoader();

export const usePersonalStore = defineStore<
  'personal',
  IPersonal,
  IPersonalGetters,
  IPersonalActions
>('personal', {
  state: (): IPersonal => ({
    LANGUAGE_ID: '',
    actions: hydrated<IPersonal['actions']>(),
    authInfo: hydrated<IPersonal['authInfo']>(),
    authInputs: [],
    countries: [],
    forgot_password: null,
    personalCabPath: '',
    routes: hydrated<IPersonal['routes']>(),
    storePassword: {},
    errors: {},
  }),
  getters: {
    isAuthorized(state) {
      return state.authInfo?.isAuthorized;
    },
  },
  actions: {
    async getData(payload) {
      try {
        const action = payload.action as keyof IPersonalRoutes;

        if (this.routes[action]) {
          loader.start();
          const path = this.routes[action]?.path;

          if (!path) throw new Error(`Bad request path: ${path}`);

          const { status, data } = await axios.post(path, payload);

          if (status !== 200) throw new Error(`Bad request response status: ${status}`);

          this.$patch(data);
        }
      } catch (err) {
        console.error(err);
      } finally {
        loader.stop();
      }
    },
    async signUpByPhone(formData) {
      try {
        if (!this.routes?.send_sms_code) return undefined;
        loader.start();
        const sendSmsRoute = this.routes.send_sms_code;
        const { path, data } = sendSmsRoute;
        const { action, api } = data;

        formData.set('action', action);
        formData.set('api', api);
        formData.set('authType', 'registration');

        if (typeof formData.get('USER_LOGIN') === 'string') {
          formData.set('phone_number', formData.get('USER_LOGIN') as string);
        }

        const {
          status,
          data: responseData,
        }: {
          status: number;
          data: { error?: string; code?: boolean };
        } = await axios.post(path, formData);

        if (status !== 200) throw new Error(`Bad request response status: ${status}`);

        if (responseData.error) {
          this.$patch({ errors: { signUpByPhone: responseData.error } });
          return undefined;
        }
        return responseData;
      } catch (err) {
        console.error(err);
        return undefined;
      } finally {
        loader.stop();
      }
    },
    async signInByPhone(formData) {
      try {
        if (!this.routes?.send_sms_code) return undefined;
        loader.start();
        const sendSmsCode = this.routes?.send_sms_code;

        const {
          path,
          data: { action, api },
        } = sendSmsCode;

        const phone = formData.get('phone') as string;

        formData.set('action', action);
        formData.set('api', api);
        formData.set('authType', 'enter');
        formData.set('USER_LOGIN', phone);
        formData.set('phone_number', phone);

        const {
          status,
          data: responseData,
        }: {
          status: number;
          data: { error?: string; code?: boolean };
        } = await axios.post(path, formData);

        if (status !== 200) throw new Error(`Bad request response status: ${status}`);

        if (responseData.error) {
          this.$patch({ errors: { signInByPhone: responseData.error } });
          return undefined;
        }
        return responseData;
      } catch (err) {
        console.error(err);
        return undefined;
      } finally {
        loader.stop();
      }
    },
    async confirmPhone(formData, callBack) {
      try {
        if (!this.routes.sms_process_login) return;
        loader.start();
        const smsProcessLogin = this.routes.sms_process_login;

        const path = smsProcessLogin?.path;
        const api = smsProcessLogin?.data?.api;
        const action = smsProcessLogin?.data?.action;

        formData.set('action', action);
        formData.set('api', api);

        const {
          status,
          data,
        }: {
          status: number;
          // eslint-disable-next-line camelcase
          data: { error?: string; code?: boolean; user_email?: string; isAuthorized?: boolean };
        } = await axios.post(path, formData);

        if (status !== 200) throw new Error(`Bad request response status: ${status}`);

        if (data.error) {
          this.$patch({ errors: { confirmPhone: data.error } });
        } else if (data.isAuthorized) {
          if (typeof callBack === 'function') {
            callBack({
              user_phone: formData.get('phone') as string,
              user_email: data.user_email,
            });
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        loader.stop();
      }
    },
    async signInByPhonePassword(formData) {
      try {
        if (!this.routes?.login_by_phone_and_password) return undefined;
        loader.start();
        const loginByPhoneAndPassword = this.routes?.login_by_phone_and_password;
        const path = loginByPhoneAndPassword?.path;
        const action = loginByPhoneAndPassword?.['params-example-data']?.action;

        formData.set('action', action);

        const {
          data: { response: responseData },
        }: {
          status: number;
          data: { response: { status: boolean; text: string } };
        } = await axios.post(path, formData);

        if (responseData.status === false) {
          this.$patch({ errors: { signInByPhoneAndPassword: responseData.text } });
          return responseData;
        }
        return responseData;
      } catch (err) {
        console.error(err);
        return undefined;
      } finally {
        loader.stop();
      }
    },
    // restore password by email
    async sendPasswordRecoveryCheckWordToUserEmail(formData) {
      try {
        if (!this.routes?.sendPasswordRecoveryCheckWordToUserEmail) return undefined;
        loader.start();

        const rout = this.routes.sendPasswordRecoveryCheckWordToUserEmail;
        const path = rout?.path;
        const action = rout?.['params-example-data']?.action;

        formData.set('action', action);

        const {
          data: { response: responseData },
        }: {
          data: { response: { status: boolean; text: string } };
        } = await axios.post(path, formData);

        if (responseData.status === false) {
          this.$patch({ errors: { forgetPasswordEmail: responseData.text } });
        }
        return responseData;
      } catch (err) {
        console.error(err);
        return undefined;
      } finally {
        loader.stop();
      }
    },
    async doPasswordRecoveryByCheckWordAndEmail(formData) {
      try {
        if (!this.routes?.doPasswordRecoveryByCheckWordAndEmail) return undefined;
        loader.start();

        const rout = this.routes?.doPasswordRecoveryByCheckWordAndEmail;

        const path = rout?.path;
        const action = rout?.['params-example-data']?.action;

        formData.set('action', action);

        const {
          data: { response: responseData },
        }: {
          data: { response: { status: boolean; text: string } };
        } = await axios.post(path, formData);
        if (responseData.status === false) {
          this.$patch({ errors: { forgetPasswordRecovery: responseData.text } });
        }
        return responseData;
      } catch (err) {
        console.error(err);
        return undefined;
      } finally {
        loader.stop();
      }
    },
    // registration global
    async sendRegisterLinkToGlobalUser(formData) {
      try {
        if (!this.routes?.sendRegisterLinkToGlobalUser) return undefined;
        loader.start();

        const rout = this.routes?.sendRegisterLinkToGlobalUser;
        const path = rout?.path;
        const action = rout?.['params-example-data']?.action;

        formData.set('action', action);

        const {
          data: { response: responseData },
        }: {
          data: { response: { status: boolean; text: string } };
        } = await axios.post(path, formData);

        if (responseData.status === false) {
          this.$patch({ errors: { registerSendLink: responseData.text } });
        }
        return responseData;
      } catch (err) {
        console.error(err);
        return undefined;
      } finally {
        loader.stop();
      }
    },
    // register Ukraine
    // async registerUkraine(formData) {
    //   try {
    //     if (!this.routes?.register) return undefined;
    //     const rout = this.routes?.register;
    //     const path = rout?.path;
    //     const action = rout?.data?.action;
    //
    //     formData.set('action', action);
    //
    //     const {
    //       data: { response: responseData },
    //     }: {
    //       data: { response: { status: boolean; text: string } };
    //     } = await axios.post(path, formData);
    //
    //     if (responseData?.status === false) {
    //       this.$patch({ errors: { registerUkraine: responseData.text } });
    //     }
    //     return responseData;
    //   } catch (err) {
    //     console.error(err);
    //     return undefined;
    //   } finally {
    //     loader.stop();
    //   }
    // },
  },
});
